// ============================================================================
// SalaryPeriodPicker (P-CE8, GC-E) — selecteur de periode `?year=&start=&end=`
// ============================================================================
// Port du PoC components/salary/period-picker.tsx (135 L @ SHA 7e40e4a). NOM
// DISTINCT du PeriodPicker FINANCE de gest (input MOIS `?period=YYYY-MM`) : ce
// composant-ci borne une PERIODE (Annee + Du/Au) a l'annee choisie, avec un
// bouton « Annee pleine » (P-CE8 : le MASTER listait « PeriodPicker (finance) »
// reutilisable — c'etait un drift, la page reel exige le composant PoC salaire).
//
// TRANSPOSITION (§GC-E) : `router.replace`/`useTransition` (Next) -> setSearchParams
// react-router (replace : la periode ne pollue pas l'historique) ; Input/Select
// shadcn -> natifs style GS_COMPONENTS.input (motif YearPicker gest) ; libelles
// FR en dur -> i18n FR+EN. Preserve les AUTRES params (dont `status`). `years`
// attendu deja trie (desc). Regle #4 : zero emoji. Regle #28 : tokens dark-aware.
//
// Bornes : `start`/`end` sont deja clippes SERVEUR a l'annee (M-681) ; on remet
// les bornes HTML (min/max) pour guider la saisie et on delegue la validation au
// serveur (une saisie hors annee / mal ordonnee retombe sur l'annee pleine).

import { useSearchParams } from 'react-router-dom';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';

export interface SalaryPeriodPickerProps {
  /** Annees selectionnables (desc, ∪ annee courante) — resolues serveur. */
  years: number[];
  /** Annee RETENUE par le serveur. */
  year: number;
  /** Debut de periode retenu serveur (ISO `YYYY-MM-DD`, clippe a l'annee). */
  start: string;
  /** Fin de periode retenue serveur (ISO `YYYY-MM-DD`, clippe a l'annee). */
  end: string;
  className?: string;
}

export function SalaryPeriodPicker({ years, year, start, end, className }: SalaryPeriodPickerProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const isFullYear = start === yearStart && end === yearEnd;

  // Applique une mutation des params et rejoue la lecture serveur (replace).
  function push(mutate: (params: URLSearchParams) => void) {
    const params = new URLSearchParams(searchParams);
    mutate(params);
    setSearchParams(params, { replace: true });
  }

  function onYear(next: string) {
    const y = Number(next);
    if (!Number.isInteger(y)) return;
    push((p) => {
      p.set('year', String(y));
      // Nouvelle annee => retour a la periode pleine (bornes implicites).
      p.delete('start');
      p.delete('end');
    });
  }

  function onStart(value: string) {
    push((p) => {
      p.set('year', String(year));
      p.set('start', value || yearStart);
      p.set('end', end);
    });
  }

  function onEnd(value: string) {
    push((p) => {
      p.set('year', String(year));
      p.set('start', start);
      p.set('end', value || yearEnd);
    });
  }

  function onReset() {
    push((p) => {
      p.set('year', String(year));
      p.delete('start');
      p.delete('end');
    });
  }

  return (
    <div className={`flex flex-wrap items-end gap-3 text-field${className ? ` ${className}` : ''}`}>
      <label className="flex flex-col gap-1">
        <span className="text-slate-500 dark:text-slate-400">{t('gest:grilleCe.reel.picker.year')}</span>
        <select
          value={String(year)}
          onChange={(e) => onYear(e.currentTarget.value)}
          aria-label={t('gest:grilleCe.reel.picker.year')}
          className={`${GS_COMPONENTS.input} h-8 w-28 touch-manipulation py-0`}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-slate-500 dark:text-slate-400">{t('gest:grilleCe.reel.picker.from')}</span>
        <input
          type="date"
          value={start}
          min={yearStart}
          max={end}
          onChange={(e) => onStart(e.currentTarget.value)}
          aria-label={t('gest:grilleCe.reel.picker.from')}
          className={`${GS_COMPONENTS.input} h-8 w-40 touch-manipulation py-0`}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-slate-500 dark:text-slate-400">{t('gest:grilleCe.reel.picker.to')}</span>
        <input
          type="date"
          value={end}
          min={start}
          max={yearEnd}
          onChange={(e) => onEnd(e.currentTarget.value)}
          aria-label={t('gest:grilleCe.reel.picker.to')}
          className={`${GS_COMPONENTS.input} h-8 w-40 touch-manipulation py-0`}
        />
      </label>

      {!isFullYear ? (
        <button
          type="button"
          onClick={onReset}
          className="h-8 touch-manipulation text-slate-500 underline-offset-2 transition-colors hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
        >
          {t('gest:grilleCe.reel.picker.fullYear')}
        </button>
      ) : null}
    </div>
  );
}
